import { useState } from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { User } from '@apis/index'
import { useNavigate } from 'react-router-dom'
// Styled components
const ModalContainer = styled.div`
  padding: 24px;
  max-width: 400px;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`

const ModalTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 10px;
`

const ModalMessage = styled.p`
  margin: 0 0 24px 0;
  line-height: 1.5;
  color: #4a5568;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`

const BaseButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-size: 0.9rem;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid #4299e1;
    outline-offset: 2px;
  }
`

const CancelButton = styled(BaseButton)`
  background-color: #e2e8f0;
  color: #4a5568;

  &:hover:not(:disabled) {
    background-color: #cbd5e0;
  }
`

const ConfirmButton = styled(BaseButton)`
  background-color: ${(props) =>
    props.variant === 'verify' ? '#48bb78' : '#f56565'};
  color: white;

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.variant === 'verify' ? '#38a169' : '#e53e3e'};
  }
`

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Spinner = styled.div`
  border: 2px solid #f3f3f3;
  border-top: 2px solid #4299e1;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: ${spin} 1s linear infinite;
  display: inline-block;
  margin-right: 8px;
  vertical-align: middle;
`

const ErrorMessage = styled.div`
  background-color: #fed7d7;
  color: #c53030;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 0.9rem;
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`

export const StatusUpdateModal = ({ item, actionType, hideModal }) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState(null)

  const actionConfig = {
    verify: {
      title: 'Verify Operator',
      message:
        'Are you sure you want to verify this operator? This action cannot be undone.',
      status: 'Verified',
      buttonText: 'Yes, Verify',
      buttonVariant: 'verify',
    },
    reject: {
      title: 'Reject Operator',
      message:
        'Are you sure you want to reject this operator? This action cannot be undone.',
      status: 'Rejected',
      buttonText: 'Yes, Reject',
      buttonVariant: 'reject',
    },
  }

  const config = actionConfig[actionType]
  const navigate = useNavigate()
  const handleStatusUpdate = async () => {
    setIsProcessing(true)
    setError(null)

    try {
      await User.verifyOperator({
        state: 'users',
        id: item.id,
        data: { status: config.status },
      })
      hideModal()
      navigate(
        `/admin/user-management/providers?tab=${config.status === 'Rejected' ? 'Rejected%20List' : 'Provider%20List'}`,
      )

      window.location.reload()
    } catch (err) {
      setError('Failed to update status. Please try again.')
      console.error('Status update error:', err)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <ModalContainer>
      <ModalTitle>{config?.title}</ModalTitle>

      <IconWrapper>
        {actionType === 'verify' ? (
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
              fill="#48BB78"
            />
          </svg>
        ) : (
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.47 2 2 6.47 2 12C2 17.53 6.47 22 12 22C17.53 22 22 17.53 22 12C22 6.47 17.53 2 12 2ZM17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7L17 8.41L13.41 12L17 15.59Z"
              fill="#F56565"
            />
          </svg>
        )}
      </IconWrapper>

      <ModalMessage>{config?.message}</ModalMessage>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonGroup>
        <CancelButton onClick={hideModal} disabled={isProcessing}>
          Cancel
        </CancelButton>
        <ConfirmButton
          onClick={handleStatusUpdate}
          disabled={isProcessing}
          // variant={config.buttonVariant}
        >
          {isProcessing && <Spinner />}
          {config?.buttonText}
        </ConfirmButton>
      </ButtonGroup>
    </ModalContainer>
  )
}
